import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const ThemeContext = createContext();

const THEME_KEY = 'vektor_theme';
const CUSTOM_COLORS_KEY = 'vektor_custom_colors';

// Preset themes
export const presetThemes = {
  default: {
    name: 'VEKTOR Default',
    description: 'Tema oscuro con acentos verdes',
    colors: {
      primary: '#10b981',      // Emerald
      primaryHover: '#059669',
      secondary: '#06b6d4',    // Cyan
      background: '#0D1117',
      backgroundSecondary: '#1C232C',
      cardBackground: '#1C232C',
      border: '#334155',
      text: '#ffffff',
      textSecondary: '#94a3b8',
      textMuted: '#64748b',
      success: '#22c55e',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#3b82f6'
    }
  },
  ocean: {
    name: 'Ocean Blue',
    description: 'Tema azul profesional',
    colors: {
      primary: '#3b82f6',
      primaryHover: '#2563eb',
      secondary: '#06b6d4',
      background: '#0c1222',
      backgroundSecondary: '#1e293b',
      cardBackground: '#1e293b',
      border: '#334155',
      text: '#ffffff',
      textSecondary: '#94a3b8',
      textMuted: '#64748b',
      success: '#22c55e',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#60a5fa'
    }
  },
  purple: {
    name: 'Purple Night',
    description: 'Tema morado elegante',
    colors: {
      primary: '#a855f7',
      primaryHover: '#9333ea',
      secondary: '#ec4899',
      background: '#0f0a1a',
      backgroundSecondary: '#1a1425',
      cardBackground: '#1a1425',
      border: '#3b2d50',
      text: '#ffffff',
      textSecondary: '#a8a1b5',
      textMuted: '#6b6178',
      success: '#22c55e',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#818cf8'
    }
  },
  sunset: {
    name: 'Sunset Orange',
    description: 'Tema cálido naranja',
    colors: {
      primary: '#f97316',
      primaryHover: '#ea580c',
      secondary: '#eab308',
      background: '#1a0f0a',
      backgroundSecondary: '#2a1a12',
      cardBackground: '#2a1a12',
      border: '#4a3020',
      text: '#ffffff',
      textSecondary: '#b8a090',
      textMuted: '#7a6050',
      success: '#22c55e',
      warning: '#fbbf24',
      error: '#ef4444',
      info: '#fb923c'
    }
  },
  corporate: {
    name: 'Corporate',
    description: 'Tema profesional corporativo',
    colors: {
      primary: '#2563eb',
      primaryHover: '#1d4ed8',
      secondary: '#64748b',
      background: '#111827',
      backgroundSecondary: '#1f2937',
      cardBackground: '#1f2937',
      border: '#374151',
      text: '#f9fafb',
      textSecondary: '#9ca3af',
      textMuted: '#6b7280',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#3b82f6'
    }
  },
  matrix: {
    name: 'Matrix',
    description: 'Estilo hacker clásico',
    colors: {
      primary: '#22c55e',
      primaryHover: '#16a34a',
      secondary: '#4ade80',
      background: '#000000',
      backgroundSecondary: '#0a0f0a',
      cardBackground: '#0a0f0a',
      border: '#1a2f1a',
      text: '#22c55e',
      textSecondary: '#4ade80',
      textMuted: '#166534',
      success: '#22c55e',
      warning: '#84cc16',
      error: '#dc2626',
      info: '#22d3ee'
    }
  },
  light: {
    name: 'Light Mode',
    description: 'Tema claro para el día',
    colors: {
      primary: '#0d9488',
      primaryHover: '#0f766e',
      secondary: '#0891b2',
      background: '#f8fafc',
      backgroundSecondary: '#ffffff',
      cardBackground: '#ffffff',
      border: '#e2e8f0',
      text: '#0f172a',
      textSecondary: '#475569',
      textMuted: '#94a3b8',
      success: '#16a34a',
      warning: '#d97706',
      error: '#dc2626',
      info: '#2563eb'
    }
  }
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    const saved = localStorage.getItem(THEME_KEY);
    return saved && presetThemes[saved] ? saved : 'default';
  });

  const [customColors, setCustomColorsState] = useState(() => {
    try {
      const saved = localStorage.getItem(CUSTOM_COLORS_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  // Get active colors (custom or from preset)
  const getActiveColors = useCallback(() => {
    if (customColors) {
      return { ...presetThemes[currentTheme].colors, ...customColors };
    }
    return presetThemes[currentTheme].colors;
  }, [currentTheme, customColors]);

  // Apply theme.
  //
  // ⚠️ Design System v4 "Falcon" (2026-06-11): el tema visible (claro/oscuro)
  // lo gobierna AHORA el atributo `data-theme` en <html> vía hooks/useTheme.js,
  // que conmuta TODOS los tokens (styles/tokens.css). Este provider conserva su
  // API (presets de color en Settings) pero ya NO inyecta variables `--color-*`
  // inline en :root, porque pisaban el tema v4 con un preset oscuro fijo y
  // descuadraban el layout (sidebar/cards). Solo refresca meta theme-color.
  const applyTheme = useCallback((colors) => {
    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme && colors && colors.primary) {
      metaTheme.setAttribute('content', colors.primary);
    }
  }, []);

  // Apply theme on mount and when it changes
  useEffect(() => {
    const colors = getActiveColors();
    applyTheme(colors);
  }, [currentTheme, customColors, getActiveColors, applyTheme]);

  // Set theme
  const setTheme = useCallback((themeName) => {
    if (presetThemes[themeName]) {
      setCurrentTheme(themeName);
      localStorage.setItem(THEME_KEY, themeName);
      // Clear custom colors when switching themes
      setCustomColorsState(null);
      localStorage.removeItem(CUSTOM_COLORS_KEY);
    }
  }, []);

  // Set custom color
  const setCustomColor = useCallback((colorKey, value) => {
    setCustomColorsState(prev => {
      const newColors = { ...(prev || {}), [colorKey]: value };
      localStorage.setItem(CUSTOM_COLORS_KEY, JSON.stringify(newColors));
      return newColors;
    });
  }, []);

  // Reset custom colors
  const resetCustomColors = useCallback(() => {
    setCustomColorsState(null);
    localStorage.removeItem(CUSTOM_COLORS_KEY);
  }, []);

  // Get current theme info
  const getCurrentThemeInfo = useCallback(() => {
    return presetThemes[currentTheme];
  }, [currentTheme]);

  // Get all available themes
  const getAvailableThemes = useCallback(() => {
    return Object.entries(presetThemes).map(([key, theme]) => ({
      id: key,
      ...theme
    }));
  }, []);

  const value = {
    currentTheme,
    setTheme,
    customColors,
    setCustomColor,
    resetCustomColors,
    getActiveColors,
    getCurrentThemeInfo,
    getAvailableThemes,
    presetThemes
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
